/**
 * Advanced Figma Design Extractor
 * 基于现有项目的设计级别提取器
 */

import type {
  ExtractorFn,
  TraversalOptions,
  SimplifiedDesign,
  TraversalContext,
  FigmaNode,
  SimplifiedComponentDefinition,
  SimplifiedComponentSetDefinition,
} from './types';
import { extractFromDesign } from './node-walker';
import { isVisible } from './node-walker';

/**
 * Extract a complete SimplifiedDesign from raw Figma API response using extractors.
 */
export function simplifyRawFigmaObject(
  apiResponse: any,
  nodeExtractors: ExtractorFn[],
  options: TraversalOptions = {},
): SimplifiedDesign {
  // Extract components, componentSets, and raw nodes from API response
  const { metadata, rawNodes, components, componentSets, extraStyles } =
    parseAPIResponse(apiResponse);

  // Process nodes using the flexible extractor system
  const globalVars: TraversalContext["globalVars"] = { styles: {}, extraStyles };
  const { nodes: extractedNodes, globalVars: finalGlobalVars } = extractFromDesign(
    rawNodes,
    nodeExtractors,
    options,
    globalVars,
  );

  // Return complete design
  return {
    ...metadata,
    nodes: extractedNodes,
    components: simplifyComponents(components),
    componentSets: simplifyComponentSets(componentSets),
    globalVars: { styles: finalGlobalVars.styles },
  };
}

/**
 * Parse the raw Figma API response to extract metadata, nodes, and components.
 */
function parseAPIResponse(data: any) {
  const aggregatedComponents: Record<string, any> = {};
  const aggregatedComponentSets: Record<string, any> = {};
  let extraStyles: Record<string, any> = {};
  let nodesToParse: Array<FigmaNode>;

  if ("nodes" in data) {
    // GetFileNodesResponse
    const nodeResponses = Object.values(data.nodes) as any[];
    nodeResponses.forEach((nodeResponse) => {
      if (nodeResponse.components) {
        Object.assign(aggregatedComponents, nodeResponse.components);
      }
      if (nodeResponse.componentSets) {
        Object.assign(aggregatedComponentSets, nodeResponse.componentSets);
      }
      if (nodeResponse.styles) {
        Object.assign(extraStyles, nodeResponse.styles);
      }
    });
    nodesToParse = nodeResponses.map((n) => n.document).filter(isVisible);
  } else {
    // GetFileResponse
    Object.assign(aggregatedComponents, data.components || {});
    Object.assign(aggregatedComponentSets, data.componentSets || {});
    if (data.styles) {
      extraStyles = data.styles;
    }
    nodesToParse = data.document?.children?.filter(isVisible) || [];
  }

  const { name } = data;

  return {
    metadata: {
      name: name || 'Untitled',
    },
    rawNodes: nodesToParse,
    extraStyles,
    components: aggregatedComponents,
    componentSets: aggregatedComponentSets,
  };
}

/**
 * Simplify Figma components to our format
 */
function simplifyComponents(components: Record<string, any>): Record<string, SimplifiedComponentDefinition> {
  const result: Record<string, SimplifiedComponentDefinition> = {};

  for (const [id, component] of Object.entries(components)) {
    result[id] = {
      id,
      name: component.name || 'Unnamed Component',
      description: component.description,
      properties: component.componentPropertyDefinitions ? 
        Object.entries(component.componentPropertyDefinitions).map(([name, prop]: [string, any]) => ({
          name,
          value: prop.defaultValue?.toString() || '',
          type: prop.type || 'TEXT',
        })) : undefined,
    };
  }

  return result;
}

/**
 * Simplify Figma component sets to our format
 */
function simplifyComponentSets(componentSets: Record<string, any>): Record<string, SimplifiedComponentSetDefinition> {
  const result: Record<string, SimplifiedComponentSetDefinition> = {};

  for (const [id, componentSet] of Object.entries(componentSets)) {
    result[id] = {
      id,
      name: componentSet.name || 'Unnamed Component Set',
      description: componentSet.description,
      variants: Object.values(componentSet.components || {}).map((component: any) => ({
        id: component.id,
        name: component.name || 'Unnamed Variant',
        description: component.description,
        properties: component.componentPropertyDefinitions ? 
          Object.entries(component.componentPropertyDefinitions).map(([name, prop]: [string, any]) => ({
            name,
            value: prop.defaultValue?.toString() || '',
            type: prop.type || 'TEXT',
          })) : undefined,
      })),
    };
  }

  return result;
}

/**
 * Advanced extractor that includes projection analysis
 */
export function createAdvancedExtractor(): ExtractorFn {
  return (node, result, context) => {
    // Add projection analysis for layout understanding
    if (node.absoluteBoundingBox && node.children && node.children.length > 0) {
      result.projectionAnalysis = analyzeNodeProjection(node);
    }
  };
}

/**
 * Analyze node projection for layout understanding
 */
function analyzeNodeProjection(node: FigmaNode): any {
  if (!node.absoluteBoundingBox || !node.children) {
    return undefined;
  }

  const bounds = node.absoluteBoundingBox;
  const children = node.children.filter(child => child.absoluteBoundingBox);

  // Calculate visual weight based on size and content
  const visualWeight = calculateVisualWeight(node);
  
  // Determine container likelihood
  const containerLikelihood = calculateContainerLikelihood(node, children);
  
  // Determine semantic role
  const semanticRole = determineSemanticRole(node);

  // Analyze projections
  const horizontalProjection = analyzeHorizontalProjection(children, bounds);
  const verticalProjection = analyzeVerticalProjection(children, bounds);

  return {
    visualWeight,
    containerLikelihood,
    semanticRole,
    horizontalProjection,
    verticalProjection,
  };
}

/**
 * Calculate visual weight of a node
 */
function calculateVisualWeight(node: FigmaNode): number {
  let weight = 0;
  
  if (!node.absoluteBoundingBox) return 0;
  
  const { width, height } = node.absoluteBoundingBox;
  const area = width * height;
  
  // Base weight from size
  weight += Math.log(area + 1) / 10;
  
  // Add weight for visual properties
  if (node.fills && node.fills.length > 0) weight += 0.2;
  if (node.strokes && node.strokes.length > 0) weight += 0.1;
  if (node.effects && node.effects.length > 0) weight += 0.15;
  if (node.opacity && node.opacity < 1) weight -= 0.1;
  
  return Math.min(weight, 1);
}

/**
 * Calculate container likelihood
 */
function calculateContainerLikelihood(node: FigmaNode, children: FigmaNode[]): number {
  let likelihood = 0;
  
  // More children = more likely to be container
  likelihood += Math.min(children.length / 10, 0.5);
  
  // Frame types are more likely containers
  if (node.type === 'FRAME' || node.type === 'GROUP') {
    likelihood += 0.3;
  }
  
  // Auto layout increases container likelihood
  if ((node as any).layoutMode) {
    likelihood += 0.4;
  }
  
  return Math.min(likelihood, 1);
}

/**
 * Determine semantic role of node
 */
function determineSemanticRole(node: FigmaNode): 'container' | 'content' | 'decoration' | 'text' | 'image' | 'button' {
  // Text nodes
  if (node.type === 'TEXT') return 'text';
  
  // Image nodes
  if (node.type === 'RECTANGLE' && node.fills?.some((fill: any) => fill.type === 'IMAGE')) {
    return 'image';
  }
  
  // Vector/icon nodes
  if (node.type === 'VECTOR' || node.type === 'ELLIPSE') return 'decoration';
  
  // Interactive elements (heuristic)
  if (node.name.toLowerCase().includes('button') || 
      node.name.toLowerCase().includes('btn') ||
      (node as any).reactions?.length > 0) {
    return 'button';
  }
  
  // Container heuristics
  if (node.children && node.children.length > 0) return 'container';
  
  return 'content';
}

/**
 * Analyze horizontal projection
 */
function analyzeHorizontalProjection(children: FigmaNode[], bounds: any): any {
  if (children.length === 0) return undefined;
  
  // Sort children by x position
  const sortedChildren = [...children].sort((a, b) => 
    (a.absoluteBoundingBox?.x || 0) - (b.absoluteBoundingBox?.x || 0)
  );
  
  // Detect columns
  const columns = detectColumns(sortedChildren);
  
  return {
    projectionType: 'horizontal' as const,
    components: columns,
    level: 1,
    columns: columns.length,
  };
}

/**
 * Analyze vertical projection
 */
function analyzeVerticalProjection(children: FigmaNode[], bounds: any): any {
  if (children.length === 0) return undefined;
  
  // Sort children by y position
  const sortedChildren = [...children].sort((a, b) => 
    (a.absoluteBoundingBox?.y || 0) - (b.absoluteBoundingBox?.y || 0)
  );
  
  // Detect rows
  const rows = detectRows(sortedChildren);
  
  return {
    projectionType: 'vertical' as const,
    components: rows,
    level: 1,
    rows: rows.length,
  };
}

/**
 * Detect columns in horizontal layout
 */
function detectColumns(children: FigmaNode[]): any[] {
  const columns: any[] = [];
  let currentColumn: FigmaNode[] = [];
  let lastX = -Infinity;
  
  for (const child of children) {
    const x = child.absoluteBoundingBox?.x || 0;
    
    // If x position is significantly different, start new column
    if (x - lastX > 20 && currentColumn.length > 0) {
      columns.push(createProjectionComponent(currentColumn));
      currentColumn = [child];
    } else {
      currentColumn.push(child);
    }
    
    lastX = x;
  }
  
  if (currentColumn.length > 0) {
    columns.push(createProjectionComponent(currentColumn));
  }
  
  return columns;
}

/**
 * Detect rows in vertical layout
 */
function detectRows(children: FigmaNode[]): any[] {
  const rows: any[] = [];
  let currentRow: FigmaNode[] = [];
  let lastY = -Infinity;
  
  for (const child of children) {
    const y = child.absoluteBoundingBox?.y || 0;
    
    // If y position is significantly different, start new row
    if (y - lastY > 20 && currentRow.length > 0) {
      rows.push(createProjectionComponent(currentRow));
      currentRow = [child];
    } else {
      currentRow.push(child);
    }
    
    lastY = y;
  }
  
  if (currentRow.length > 0) {
    rows.push(createProjectionComponent(currentRow));
  }
  
  return rows;
}

/**
 * Create projection component from children
 */
function createProjectionComponent(children: FigmaNode[]): any {
  if (children.length === 0) return null;
  
  // Calculate bounding box for the group
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  
  for (const child of children) {
    if (child.absoluteBoundingBox) {
      const { x, y, width, height } = child.absoluteBoundingBox;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + width);
      maxY = Math.max(maxY, y + height);
    }
  }
  
  return {
    id: `projection_${children.map(c => c.id).join('_')}`,
    name: `Group of ${children.length} elements`,
    bounds: {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
    },
    children: children.map(child => ({
      id: child.id,
      name: child.name,
      type: child.type,
    })),
  };
}